import type { FC } from "react";
import { memo, useCallback } from "react";

import classNames from "classnames";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { UiInfoCircleSolidIcon } from "@jti/brand-icons/ui-info-circle-solid";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { type CartCoupon } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { cartSlice } from "@jti/store/slices/cartSlice";
import type { FieldValues } from "@jti/ui";
import { Controller, Form, Tooltip, useForm, useTranslations } from "@jti/ui";

import { PromoCodeControls } from "../PromoCodeControls";

import styles from "./PromoCodeView.module.scss";

type PromoCodeViewProps = {
  items: CartCoupon[];
  errors?: string[];
  onRemoveAllCoupons(): void;
  onRemoveCoupon(code: string): void;
  onAddCoupon(code: string): void;
};

interface FormValues extends FieldValues {
  code: string;
}

const PROMO_CODES_LIMIT = 3;

const getPromoCodeError = (errors?: string[], fallback?: string): string | undefined => {
  if (!errors?.length) return undefined;
  const message = errors.filter(Boolean).join(", ");
  return message || fallback;
};

export const PromoCodeView: FC<PromoCodeViewProps> = memo(
  ({ items, errors, onRemoveAllCoupons, onAddCoupon, onRemoveCoupon }) => {
    const { __ } = useTranslations();

    const form = useForm<FormValues>({
      defaultValues: {
        code: "",
      },
    });

    const { reset, control } = form;
    const dispatch = useDispatch();
    const couponsPending = useSelector((state) => state.cart.couponsPending);

    const handleSubmit = useCallback(
      async ({ code }: FormValues) => {
        await onAddCoupon(code);
        reset();
      },
      [onAddCoupon, reset]
    );

    const handleCollapse = () => {
      dispatch(cartSlice.actions.clearCouponErrors());
    };

    const title = (device: "mobileHidden" | "desktopHidden") => (
      <div className={classNames(styles.titleContainer, styles[device])}>
        <div className={styles.title}>{__("cart.payment_summary.voucher.title")}</div>
        <div className={styles.subtitle}>
          {__("cart.payment_summary.voucher.subtitle_closed")}
          <Tooltip
            IconSlot={() => (
              <div className={styles.infoIcon}>
                <UiInfoCircleSolidIcon />
              </div>
            )}
            wrapperClassName={styles.tooltipWrapper}
            content={<span>{__("cart.payment_summary.voucher.subtitle_closed_popover")}</span>}
          />
        </div>
      </div>
    );

    return (
      <Card
        variant="level-2"
        headerContent={
          <>
            {title("mobileHidden")}
            {title("desktopHidden")}
          </>
        }
        data-testid="promoCodeCard"
        isCollapsible
        defaultCollapsed={items.length === 0}
        onToggle={handleCollapse}
        collapseButtonAriaLabel={__("cart.payment_summary.voucher.title")}
      >
        <div className={styles.content}>
          {items.length < PROMO_CODES_LIMIT && (
            <Form form={form} onSubmit={handleSubmit} data-testid="promoCodeForm">
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <PromoCodeControls
                    {...field}
                    isError={!!errors?.length}
                    errorText={getPromoCodeError(errors, __("cart.checkout.error.promo_code"))}
                  />
                )}
              />
            </Form>
          )}

          {items.length > 0 ? (
            <div className={styles.itemsContainer}>
              <div className={styles.listHeader}>
                <div className={styles.appliedPromoCodes}>{__("cart.promo_code.applied_codes")}:</div>
                <Button
                  variant="text"
                  disabled={couponsPending}
                  onClick={onRemoveAllCoupons}
                  data-testid="removeCoupons"
                >
                  {__("cart.promo_code.clear_all_button")}
                </Button>
              </div>
              {items.map(({ label, code: couponCode }) => (
                <div className={styles.item} key={couponCode}>
                  <div className={styles.couponText}>
                    <strong className={styles.couponCode}>{couponCode}</strong>{" "}
                    {label && <span className={styles.couponLabel}>{`(${label})`}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveCoupon(couponCode)}
                    data-testid={`removeCoupon${couponCode}`}
                    disabled={couponsPending}
                    className={styles.removeBtn}
                    aria-label={__("cart.promo_code.remove_coupon", [couponCode])}
                  >
                    {<UiCrossIcon className={styles.icon} />}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Card>
    );
  }
);
